// Durable, localStorage-backed queue for the analytics SDK. This is now the SOURCE OF TRUTH
// for pending events: `emit()` appends here first, and a flush only clears an event once the
// backend has confirmed receipt. This gives at-least-once delivery across reloads/crashes/tab
// closes, at the cost of rare duplicates (acceptable — analytics is loss-tolerant, ClickHouse
// has no dedup).
//
// HIPAA: everything ever written here is the same non-PHI `AppEventPayload` the wire transport
// already sends (opaque ids, enum-like names, path-only url_path) — persisting it to
// localStorage does not change what data exists, only where it's buffered.
//
// Every function here is defensive: localStorage can throw (private browsing, quota exceeded,
// disabled by policy) or simply be absent (non-browser test/SSR contexts). No function in this
// module may ever let such an error escape — analytics must never break the app.
import type { AppEventPayload } from '@/types/analytics';

export const QUEUE_KEY = 'analytics:queue:v1';
export const MAX_STORED = 50;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function isStoredEvent(value: unknown): value is AppEventPayload {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.event_id === 'string' &&
    typeof v.event_time === 'number' &&
    typeof v.event_type === 'number' &&
    typeof v.event_name === 'string' &&
    typeof v.org_id === 'string'
  );
}

// Parses whatever is currently in localStorage without filtering, so callers that need to know
// whether filtering actually dropped anything (e.g. `loadPending`'s GC write-back) can compare
// against the true original count.
function parseStoredArray(): unknown[] {
  const raw = localStorage.getItem(QUEUE_KEY);
  if (!raw) return [];
  const parsed: unknown = JSON.parse(raw);
  return Array.isArray(parsed) ? parsed : [];
}

function readRaw(): AppEventPayload[] {
  try {
    return parseStoredArray().filter(isStoredEvent);
  } catch {
    return [];
  }
}

function writeRaw(events: AppEventPayload[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(events));
  } catch {
    /* localStorage unavailable/full — degrade gracefully, drop persistence for this write */
  }
}

/**
 * Load pending events, dropping malformed entries and pruning any event older than
 * `MAX_AGE_MS`. Never throws — returns `[]` on any error.
 *
 * This is also the GC point for stale/malformed data: if filtering actually dropped anything,
 * the pruned set is written back so those entries don't linger in localStorage on an idle tab
 * that never appends/removes (which would otherwise be the only other write paths).
 */
export function loadPending(): AppEventPayload[] {
  try {
    const originalCount = parseStoredArray().length;
    const cutoff = Date.now() - MAX_AGE_MS;
    const events = readRaw().filter(e => e.event_time >= cutoff);
    if (events.length !== originalCount) writeRaw(events);
    return events;
  } catch {
    return [];
  }
}

/**
 * Append a single event to the store, keeping only the most recent `MAX_STORED` events
 * (oldest dropped first). Never throws.
 *
 * NOTE: this read-modify-write is NOT atomic across tabs (unlike `flush`'s drain+send, which is
 * wrapped in a Web Lock in index.ts). Two tabs appending/removing at nearly the same instant can
 * rarely clobber each other's write. This is accepted: analytics is loss-tolerant and ClickHouse
 * has no dedup, so an occasional lost event here is preferable to the complexity/latency of
 * lock-guarding every single append.
 */
export function append(event: AppEventPayload): void {
  try {
    const events = readRaw();
    events.push(event);
    const trimmed = events.length > MAX_STORED ? events.slice(events.length - MAX_STORED) : events;
    writeRaw(trimmed);
  } catch {
    /* never throw from analytics */
  }
}

/**
 * Remove events by id — called after a successful send. Storage is authoritative for retry, so
 * anything not removed here will be resent on the next flush.
 *
 * NOTE: same cross-tab caveat as `append` above — this read-modify-write is not atomic across
 * tabs outside of the Web-Locked flush path. Accepted for the same loss-tolerant reason.
 */
export function removeByIds(ids: string[]): void {
  try {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const remaining = readRaw().filter(e => !idSet.has(e.event_id));
    writeRaw(remaining);
  } catch {
    /* never throw from analytics */
  }
}

/** Alias of {@link loadPending} for the flusher — reads the current durable snapshot. */
export function snapshot(): AppEventPayload[] {
  return loadPending();
}
